import { useQuery } from "@tanstack/react-query";
import { useTransition } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useLocale, useTranslations } from "use-intl";
import { Dumbbell, Footprints, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { lt } from "@liftquest/shared/localized";
import { Page, PageHeader, Section } from "@/components/app/page-header";
import { PageLoading } from "@/components/app/states";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { importSharedPlan } from "@/lib/actions";
import { api, ApiError, call } from "@/lib/api";
import { qk } from "@/lib/queries";

/** A plan someone shared by link: preview and "add to my plans". */
export function SharedPlanPage() {
  const { token = "" } = useParams();
  const locale = useLocale();
  const t = useTranslations("share");
  const tc = useTranslations("common");
  const tp = useTranslations("planEditor");
  const navigate = useNavigate();
  const [pending, start] = useTransition();
  const shared = useQuery({
    queryKey: qk.shared(token),
    queryFn: () => call(api.shared[":token"].$get({ param: { token } })),
    retry: (n, e) => !(e instanceof ApiError && e.status === 404) && n < 2,
  });
  if (shared.isPending) return <PageLoading />;
  if (!shared.data) {
    return (
      <Page>
        <PageHeader title={t("title")} />
        <p className="mt-6 rounded-2xl border border-dashed p-6 text-center text-sm text-muted-foreground">{t("gone")}</p>
        <Button asChild variant="outline" className="mt-4 w-full">
          <Link to="/plans">{t("toPlans")}</Link>
        </Button>
      </Page>
    );
  }

  const { plan, exercises, owner, own } = shared.data;
  const name = (key: string) => lt(exercises[key]?.name, locale);
  const add = () =>
    start(async () => {
      try {
        const { id } = await importSharedPlan(token);
        toast.success(t("added"));
        navigate(`/plans/${id}`);
      } catch {
        toast.error(tc("error"));
      }
    });

  return (
    <Page>
      <PageHeader title={lt(plan.name, locale)} subtitle={own ? t("yours") : t("sharedBy", { name: owner })} />
      {lt(plan.description, locale) && <p className="mt-3 text-sm text-muted-foreground">{lt(plan.description, locale)}</p>}
      <Button size="lg" className="mt-5 h-12 w-full rounded-2xl text-base" disabled={pending} onClick={add}>
        {pending ? <Loader2 className="animate-spin" /> : <Plus />} {t("add")}
      </Button>
      <p className="mt-2 text-center text-xs text-muted-foreground">{t("addHint")}</p>
      <Section title={tp("workouts")}>
        <ul className="space-y-2">
          {plan.workouts.map((w, i) => (
            <li key={i}>
              <Card className="gap-2 px-4 py-3">
                <div className="flex items-center gap-2">
                  {w.kind === "cardio" ? <Footprints className="size-4 text-chart-3" /> : <Dumbbell className="size-4 text-primary" />}
                  <p className="font-semibold">{lt(w.name, locale)}</p>
                </div>
                {lt(w.focus, locale) && <p className="text-xs text-muted-foreground">{lt(w.focus, locale)}</p>}
                <ol className="divide-y text-sm">
                  {w.exercises.map((we, j) => (
                    <li key={j} className="flex items-center justify-between gap-3 py-1.5">
                      <span className="min-w-0 truncate">{name(we.exercise)}</span>
                      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                        {we.sets} × {we.targetMin}–{we.targetMax}
                      </span>
                    </li>
                  ))}
                </ol>
              </Card>
            </li>
          ))}
        </ul>
      </Section>
    </Page>
  );
}
